/* eslint-disable react/prop-types */

import moment from 'moment-timezone';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../api/reports';
import { InfoLabel, Loader, Pagination, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { journalsTableHeaders } from '../../../constants/table-headers';
import { fetchMultiSelectData, journalsTitles } from '../../../utils/constant';
import * as handlers from '../../../utils/handlers';
import DownloadModal from '../../reports/downloadModal/downloadModal';
import onDownloadClick from '../../reports/generateReport/onDownloadClickHandler';
import ItemEmptyTile from '../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../reports/toolbar/toolbar';

export const TableDataContext = createContext([]);

const TableComponent = ({ children, totalResults, tableData, tableName, fromDate, toDate }) => {
  const [journalTableData, setTableJournalData] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  const filterData = useSelector((state) => state.filters.filtersForMultiSelect);
  const defaultFilters = useSelector((state) => state.filters.defaultMultiSelectFilters);
  const filterExtraData = useSelector((state) => state?.filters?.filtersExtraData);
  const { page, limit, setPage, setLimit } = usePagination();
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData({ page: 1, limit: 5 });
  }, [filterData]);

  useEffect(() => {
    if (applyFilters) {
      getUpdatedTableData();
    }
  }, [applyFilters]);

  useEffect(() => {
    setPage(1);
    setLimit(5);
    setTotalCount(totalResults);
    setTableJournalData(tableData);
  }, [tableData, totalResults, setPage, setLimit]);

  const toggleDownloadModal = () => {
    if (journalTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  const getUpdatedTableData = async (payloadData) => {
    if (payloadData) {
      setPage(payloadData?.page);
      setLimit(payloadData?.limit);
    }
    setLoading(true);
    setApplyFilters(false);

    const requestParams = {
      ...fetchMultiSelectData(filterData, defaultFilters),
      startDate: fromDate,
      endDate: toDate,
      timezone: moment.tz.guess(),
    };
    if (filterExtraData?.journalReportType === 'trustJournal') {
      requestParams.team = '';
      requestParams.reportingManager = '';
    }
    try {
      const { data } = await getReportData(tableName, {
        ...requestParams,
        page: payloadData?.page || page,
        limit: payloadData?.limit || limit,
      });
      const response = await getTotalCount(tableName, { ...requestParams });
      setTotalCount(response?.data?.data?.totalCount);
      setTableJournalData(data?.data?.items || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="j-gl-heading mt-5 d-inline-block">
        <InfoLabel label={journalsTitles[tableName]} />
      </div>
      <div className="d-inlin-block toolbar-down-repot">
        <Toolbar
          optionTwoLabel="Download Report"
          optionTwoIcon="FiDownload"
          onOptionTwoClick={toggleDownloadModal}
          isTableDataPresent={journalTableData?.length}
        />
      </div>
      <hr />
      {journalTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll journal-table-res">
            <Table className="journal-table" responsive headers={journalsTableHeaders[tableName]}>
              <tbody>
                <TableDataContext.Provider value={journalTableData}>
                  {children}
                </TableDataContext.Provider>
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              page={page}
              limit={limit}
              totalResults={totalCount}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={tableName}
        urlParams={{
          startDate: fromDate,
          endDate: toDate,
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
      />
    </>
  );
};

const JournalHoc =
  (WrappedComponent) =>
  ({ tableData, fromDate, toDate, totalResults, tableName }) => {
    return (
      <TableComponent
        tableData={tableData}
        totalResults={totalResults}
        tableName={tableName}
        fromDate={fromDate}
        toDate={toDate}
      >
        <WrappedComponent />
      </TableComponent>
    );
  };

export default JournalHoc;
